import React from "react";
import { Calendar, MessageSquareOff, Sparkles, ArrowRight, PlayCircle } from "lucide-react";
import { handlePrimaryCta, PRIMARY_CTA_LABEL } from "@/lib/contact";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 overflow-hidden bg-nav-950"
    >
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-brand/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute top-32 -left-40 h-[460px] w-[460px] rounded-full bg-[#EC4899]/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 30%, black 40%, transparent 75%)",
        }}
      />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="eyebrow" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              Built for businesses that run on bookings, sign-ups & members
            </span>
            <h1
              data-testid="hero-headline"
              className="mt-5 h-display text-[40px] leading-[1.05] sm:text-6xl lg:text-7xl"
            >
              Stop Losing{" "}
              <span className="relative whitespace-nowrap">
                Visitors
                <svg
                  aria-hidden="true"
                  viewBox="0 0 200 14"
                  className="absolute -bottom-2 left-0 w-full"
                >
                  <path
                    d="M2 11 C 60 2, 140 2, 198 11"
                    stroke="#5B5BFF"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              .{" "}
              <span className="text-brand">Start booking them.</span>
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-6 text-lg sm:text-xl text-nav-300 max-w-xl leading-relaxed"
            >
              Whether visitors find you through Google, social, a flyer, a
              bulletin, or a friend — your website should convert them. We
              build booking funnels that turn high-intent visitors into{" "}
              <span className="text-white font-semibold">
                booked appointments, registrations, and members
              </span>{" "}
              — automatically.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#contact"
                onClick={handlePrimaryCta}
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white
                  px-8 py-5 text-lg sm:text-xl font-bold
                  shadow-[0_20px_50px_-10px_rgba(124,58,237,0.65)]
                  hover:shadow-[0_26px_60px_-8px_rgba(236,72,153,0.6)] hover:scale-[1.03]
                  transition-all duration-200 relative"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] opacity-40 blur-xl animate-pulse"
                />
                <Sparkles className="h-5 w-5 relative" />
                <span className="relative">{PRIMARY_CTA_LABEL}</span>
              </a>
              <a
                href="#solution"
                data-testid="hero-how-it-works"
                className="btn-secondary text-base"
              >
                <PlayCircle className="h-5 w-5" />
                How it works
              </a>
            </div>
            <p className="mt-3 text-sm text-nav-400">
              <span className="text-white font-semibold">We build your funnel first. You decide after.</span>{" "}
              Free demo · No card · Yours to keep.
            </p>

            <div className="mt-10 pt-6 border-t border-nav-800 grid grid-cols-3 gap-4 max-w-lg">
              <div>
                <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-bold uppercase tracking-wider">
                  <Calendar className="h-3.5 w-3.5" />
                  Bookings
                </div>
                <div className="mt-1 font-display text-2xl font-bold text-white">
                  +3.4×
                </div>
                <div className="text-[11px] text-nav-500">avg lift</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-brand text-xs font-bold uppercase tracking-wider">
                  <MessageSquareOff className="h-3.5 w-3.5" />
                  Inbox load
                </div>
                <div className="mt-1 font-display text-2xl font-bold text-white">
                  -68%
                </div>
                <div className="text-[11px] text-nav-500">fewer follow-ups</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-amber-400 text-xs font-bold uppercase tracking-wider">
                  <Sparkles className="h-3.5 w-3.5" />
                  Launch
                </div>
                <div className="mt-1 font-display text-2xl font-bold text-white">
                  24 hrs
                </div>
                <div className="text-[11px] text-nav-500">fast-track</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up">
            <div className="relative">
              <div className="rounded-2xl bg-nav-900 border border-nav-800 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)] overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-3 bg-nav-800 border-b border-nav-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  <div className="ml-3 flex-1 h-5 rounded bg-nav-900 border border-nav-700 text-[10px] text-nav-500 grid place-items-center">
                    yourstudio.com/book
                  </div>
                </div>
                <div className="relative aspect-[4/3] bg-gradient-to-br from-nav-900 to-black text-white">
                  <img
                    src="/hero-studio.jpg"
                    alt="Booking funnel mockup"
                    className="absolute inset-0 h-full w-full object-cover opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-nav-950 via-nav-950/70 to-nav-950/20" />
                  <div className="absolute inset-0 p-5 sm:p-6 flex flex-col justify-end">
                    <span className="text-[11px] uppercase tracking-widest text-brand font-semibold">
                      Sample Booking Funnel
                    </span>
                    <h3 className="font-display font-bold text-2xl mt-1 text-white">
                      Bloom Lash Studio
                    </h3>
                    <p className="text-sm text-nav-300 mt-1">
                      A clean 3-step booking flow. Visitors register
                      themselves in — no chasing, no missed messages.
                    </p>
                    <div className="mt-3 inline-flex w-fit items-center gap-2 rounded-full bg-brand text-white text-[11px] font-bold px-3 py-1.5">
                      Book a session <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-3 sm:-right-8 top-1/2 -translate-y-1/2 bg-nav-900 border border-nav-800 rounded-xl shadow-2xl p-4 w-48">
                <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider">
                  <Calendar className="h-3.5 w-3.5" />
                  Bookings/wk
                </div>
                <div className="mt-1 text-3xl font-display font-bold tracking-tight text-white">
                  +18
                </div>
                <div className="text-xs text-nav-400">Avg in first 30 days</div>
              </div>
              <div className="absolute -left-3 sm:-left-6 -top-4 bg-brand text-white rounded-xl shadow-2xl px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-white/80 font-semibold">
                  No-Show Rate
                </div>
                <div className="text-lg font-display font-bold">↓ 41%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
